import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const Pagination = ({ 
  currentPage, 
  lastPage, 
  perPage, 
  total, 
  onPageChange,
  className = "",
  showResultsCounter = true,
  scrollToTop = true
}) => {
  // Don't render if there's only one page or no results
  if (lastPage <= 1) return null;

  const handlePageChange = (page) => {
    onPageChange(page);
    // Smooth scroll to top if enabled
    if (scrollToTop) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const getVisiblePages = () => {
    const maxVisible = 5;
    const pages = [];
    
    if (lastPage <= maxVisible) {
      // Show all pages if total is less than max visible
      for (let i = 1; i <= lastPage; i++) {
        pages.push(i);
      }
    } else {
      // Show first few pages
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(lastPage);
      }
      // Show middle pages
      else if (currentPage >= lastPage - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = lastPage - 3; i <= lastPage; i++) {
          pages.push(i);
        }
      }
      // Show current page in middle
      else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(lastPage);
      }
    }
    
    return pages;
  };

  const visiblePages = getVisiblePages();

  return (
    <div className={`flex items-center justify-center mt-8 ${className}`}>
      <div className="flex items-center space-x-2">
        {/* Previous Button */}
        <button
          onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
          disabled={currentPage === 1}
          className={`flex items-center px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
            currentPage === 1
              ? 'text-gray-400 bg-white border-gray-200 cursor-not-allowed'
              : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50 hover:border-gray-400'
          }`}
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          Previous
        </button>
        
        {/* Page Numbers */}
        <div className="flex items-center space-x-1">
          {visiblePages.map((page, index) => {
            if (page === '...') {
              return (
                <span key={`ellipsis-${index}`} className="px-2 text-gray-500">
                  ...
                </span>
              );
            }
            
            const isActive = page === currentPage;
            return (
              <button
                key={page}
                onClick={() => handlePageChange(page)}
                className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  isActive
                    ? 'bg-black text-white border border-black'
                    : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 hover:border-gray-400'
                }`}
              >
                {page}
              </button>
            );
          })}
        </div>
        
        {/* Next Button */}
        <button
          onClick={() => handlePageChange(Math.min(lastPage, currentPage + 1))}
          disabled={currentPage === lastPage}
          className={`flex items-center px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
            currentPage === lastPage
              ? 'text-gray-400 bg-white border-gray-200 cursor-not-allowed'
              : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50 hover:border-gray-400'
          }`}
        >
          Next
          <ChevronRight className="w-4 h-4 ml-1" />
        </button>
      </div>
      
      {/* Results Counter */}
      {showResultsCounter && (
        <div className="ml-6 text-sm text-gray-500">
          Showing {((currentPage - 1) * perPage) + 1} to {Math.min(currentPage * perPage, total)} of {total} results
        </div>
      )}
    </div>
  );
};

export default Pagination;
